import { makeAutoObservable } from 'mobx';
import {
  ScanIqCustomerPersonalData,
  ScanIqCustomerProfileInfo,
} from '../interfaces/ScanIqCustomerProfileInfo';
import { resetAllStore } from '../../shared/stores/resetAllStore';
import { unreadScansCountStore } from '../../shared/stores/unreadScansCountStore';
import { scanIqCustomerProfileService } from '../services/scanIqCustomerProfileService';

class ScanIqCustomerProfileStore {
  customerId: number | null = null;
  customerProfileInfo: ScanIqCustomerProfileInfo | null = null;
  entityId: number | null = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setDefaultValues() {
    this.customerProfileInfo = null;
    this.entityId = null;
  }

  setCustomerProfileInfo(customerProfileInfo: ScanIqCustomerProfileInfo) {
    this.customerProfileInfo = customerProfileInfo;
  }

  setPersonalData(personalData: ScanIqCustomerPersonalData) {
    if (this.customerProfileInfo) {
      this.customerProfileInfo.personalData = personalData;
    }
  }

  async getCustomerProfileInfo(entityId: number, customerId: number) {
    const data = await scanIqCustomerProfileService.getCustomerProfileInfo(
      entityId,
      customerId,
    );

    // Mark this customer as "seen" on the backend.
    // This clears the ScanIQ "unread/new scan" state for this customer in widgets that rely on it
    // (for example, the "Requires Attention" unread scans list or badge counter in the sidebar).
    await unreadScansCountStore.markScanIqCustomerScanSeen(customerId);

    this.setCustomerProfileInfo(data);
    this.setEntityId(entityId);
    this.setCustomerId(customerId);
  }

  async refreshCustomerProfileInfo() {
    if (!this.customerId || !this.entityId) {
      throw new Error('Customer profile info or entity id is not set');
    }

    const data = await scanIqCustomerProfileService.getCustomerProfileInfo(
      this.entityId,
      this.customerId,
    );

    this.setCustomerProfileInfo(data);
  }

  async updateCustomerProfileInfo(data: ScanIqCustomerPersonalData) {
    if (!this.customerProfileInfo) {
      throw new Error('Customer profile info is not set');
    }

    const updatedData = await scanIqCustomerProfileService.updateCustomerPersonalData(
      this.customerProfileInfo.id,
      data,
    );

    this.setPersonalData(updatedData);
  }

  private setEntityId(entityId: number) {
    this.entityId = entityId;
  }

  private setCustomerId(customerId: number) {
    this.customerId = customerId;
  }
}

const scanIqCustomerProfileStore = new ScanIqCustomerProfileStore();
resetAllStore.addResetMethod(scanIqCustomerProfileStore.setDefaultValues);

export { scanIqCustomerProfileStore, ScanIqCustomerProfileStore };
